import { useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentUser, AuthUser, signOut, signIn, confirmSignIn, SignInInput } from '@aws-amplify/auth';
import { User } from 'shared/types/platformTypes';
import { getCachedUserProfile, clearUserProfileCache } from '../utils/authUtils';
import logger from '../utils/logger';

export const useAuth = () => {
  const [currentUser2, setCurrentUser] = useState<AuthUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState({ show: false, signIn: false });
  const hasInitialFetch = useRef(false);

  const fetchUserData = useCallback(async (force = false) => {
    // Prevent duplicate calls unless forced
    if (!force && hasInitialFetch.current) {
      return;
    }

    try {
      setLoading(true);
      hasInitialFetch.current = true;

      // First, check if there's a current user
      const user = await getCurrentUser().catch(() => null);

      if (user) {
        setCurrentUser(user);

        // Use shared getCachedUserProfile function
        const profile = await getCachedUserProfile(user.userId, force);
        setUserProfile(profile);
      } else {
        clearAuthState();
        hasInitialFetch.current = false;
      }
    } catch (error) {
      logger.error('Error fetching user data:', error);
      clearAuthState();
      hasInitialFetch.current = false;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only run on mount, not when fetchUserData changes
    fetchUserData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAuthState = useCallback(() => {
    setCurrentUser(null);
    setUserProfile(null);
    setShowAuth({ show: false, signIn: false });
    hasInitialFetch.current = false;
    clearUserProfileCache(); // Use shared clear function
  }, []);

  const logout2 = useCallback(async () => {
    try {
      await signOut();
      setCurrentUser(null);
      setUserProfile(null);
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  }, []);


  const loginWithMagicLink = useCallback(async (email: string, token: string) => {
    try {
      const signInInput: SignInInput = {
        username: email,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP'
        }
      };

      const signInOutput = await signIn(signInInput);

      if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        const confirmSignInOutput = await confirmSignIn({ challengeResponse: token });

        if (confirmSignInOutput.isSignedIn) {
          await fetchUserData(true); // Force refresh after login
          return true;
        }
      }

      return false;
    } catch (error) {
      logger.error('Error logging in with magic link:', error);
      return false;
    }
  }, [fetchUserData]);

  return {
    currentUser2,
    userProfile,
    loading,
    fetchUserData,
    logout2,
    showAuth,
    setShowAuth,
    loginWithMagicLink
  };
};